import React, { useEffect, useRef, useState } from 'react';

/* ═══════════════════════════════════════════════════════════════════════════
   Distorsión líquida sobre una imagen o un vídeo
   ═══════════════════════════════════════════════════════════════════════════

   Una simulación de fluidos en WebGL: el puntero inyecta velocidad en una
   rejilla, se resuelve la presión, y el campo resultante desplaza los píxeles
   de la fuente. El agua sigue moviéndose un momento después de que paras.

   Los shaders son los que aportó el equipo (Originkit «Liquid Distortion»).
   Lo que hubo que rehacer, y por qué:

   1. **De imagen a VÍDEO.** El original carga un `.jpg` a la textura UNA vez.
      Un vídeo son 30 texturas por segundo: hay que volver a subir el fotograma
      actual en cada cuadro con `texImage2D`. Sin eso, el efecto distorsionaría
      un fotograma congelado sobre un vídeo en marcha.
   2. **El bucle NO corre siempre.** El original deja un `requestAnimationFrame`
      girando de por vida. Aquí una simulación de fluidos con 16 iteraciones de
      presión MÁS la subida del fotograma es trabajo constante de GPU sobre la
      primera pantalla que ve el visitante — justo donde peor cae. Solo corre
      con el puntero dentro del hero, y sigue unos cuadros más para que el agua
      se asiente.
   3. **Se rinde antes de empezar** si no hay ratón (en el teléfono no hay
      puntero que seguir y el coste no se justifica), si no hay WebGL, o si
      falta `OES_texture_float`, que es lo que necesitan los búferes de la
      simulación. En cualquiera de esos casos se queda el vídeo tal cual.

   No se consulta `prefers-reduced-motion`: aquí no se mueve nada solo. La
   distorsión la provoca la mano del usuario y desaparece cuando la retira.
   ═══════════════════════════════════════════════════════════════════════════ */

const VERT = `
precision highp float;
varying vec2 vUv;
attribute vec2 a_position;
varying vec2 vL;
varying vec2 vR;
varying vec2 vT;
varying vec2 vB;
uniform vec2 u_texel;

void main () {
  vUv = .5 * (a_position + 1.);
  vL = vUv - vec2(u_texel.x, 0.);
  vR = vUv + vec2(u_texel.x, 0.);
  vT = vUv + vec2(0., u_texel.y);
  vB = vUv - vec2(0., u_texel.y);
  gl_Position = vec4(a_position, 0., 1.);
}
`;

const FRAG_ADVECT = `
precision highp float;
precision highp sampler2D;
varying vec2 vUv;
uniform sampler2D u_velocity_texture;
uniform sampler2D u_input_texture;
uniform vec2 u_texel;
uniform vec2 u_output_textel;
uniform float u_dt;
uniform float u_dissipation;

vec4 bilerp (sampler2D sam, vec2 uv, vec2 tsize) {
  vec2 st = uv / tsize - 0.5;
  vec2 iuv = floor(st);
  vec2 fuv = fract(st);
  vec4 a = texture2D(sam, (iuv + vec2(0.5, 0.5)) * tsize);
  vec4 b = texture2D(sam, (iuv + vec2(1.5, 0.5)) * tsize);
  vec4 c = texture2D(sam, (iuv + vec2(0.5, 1.5)) * tsize);
  vec4 d = texture2D(sam, (iuv + vec2(1.5, 1.5)) * tsize);
  return mix(mix(a, b, fuv.x), mix(c, d, fuv.x), fuv.y);
}

void main () {
  vec2 coord = vUv - u_dt * bilerp(u_velocity_texture, vUv, u_texel).xy * u_texel;
  vec4 velocity = bilerp(u_input_texture, coord, u_output_textel);
  gl_FragColor = u_dissipation * velocity;
}
`;

const FRAG_DIVERGENCE = `
precision highp float;
precision highp sampler2D;
varying highp vec2 vUv;
varying highp vec2 vL;
varying highp vec2 vR;
varying highp vec2 vT;
varying highp vec2 vB;
uniform sampler2D u_velocity_texture;

void main () {
  float L = texture2D(u_velocity_texture, vL).x;
  float R = texture2D(u_velocity_texture, vR).x;
  float T = texture2D(u_velocity_texture, vT).y;
  float B = texture2D(u_velocity_texture, vB).y;
  gl_FragColor = vec4(.25 * (R - L + T - B), 0., 0., 1.);
}
`;

const FRAG_PRESSURE = `
precision highp float;
precision highp sampler2D;
varying highp vec2 vUv;
varying highp vec2 vL;
varying highp vec2 vR;
varying highp vec2 vT;
varying highp vec2 vB;
uniform sampler2D u_pressure_texture;
uniform sampler2D u_divergence_texture;

void main () {
  float L = texture2D(u_pressure_texture, vL).x;
  float R = texture2D(u_pressure_texture, vR).x;
  float T = texture2D(u_pressure_texture, vT).x;
  float B = texture2D(u_pressure_texture, vB).x;
  float divergence = texture2D(u_divergence_texture, vUv).x;
  gl_FragColor = vec4((L + R + B + T - divergence) * .25, 0., 0., 1.);
}
`;

const FRAG_GRAD_SUB = `
precision highp float;
precision highp sampler2D;
varying highp vec2 vUv;
varying highp vec2 vL;
varying highp vec2 vR;
varying highp vec2 vT;
varying highp vec2 vB;
uniform sampler2D u_pressure_texture;
uniform sampler2D u_velocity_texture;

void main () {
  float L = texture2D(u_pressure_texture, vL).x;
  float R = texture2D(u_pressure_texture, vR).x;
  float T = texture2D(u_pressure_texture, vT).x;
  float B = texture2D(u_pressure_texture, vB).x;
  vec2 velocity = texture2D(u_velocity_texture, vUv).xy;
  velocity.xy -= vec2(R - L, T - B);
  gl_FragColor = vec4(velocity, 0., 1.);
}
`;

const FRAG_POINT = `
precision highp float;
precision highp sampler2D;
varying vec2 vUv;
uniform sampler2D u_input_texture;
uniform float u_ratio;
uniform vec3 u_point_value;
uniform vec2 u_point;
uniform float u_point_size;

void main () {
  vec2 p = vUv - u_point.xy;
  p.x *= u_ratio;
  vec3 splat = .6 * pow(2., -dot(p, p) / u_point_size) * u_point_value;
  vec3 base = texture2D(u_input_texture, vUv).xyz;
  gl_FragColor = vec4(base + splat, 1.);
}
`;

/* Salida. Diferencias con el original: se añadió `u_img_offset` para respetar
   el encuadre del vídeo (que va a 58 % horizontal, no centrado) y se quitó el
   desvanecido del borde — aquí el lienzo cubre el hero entero y un borde
   difuminado dejaría ver una franja del vídeo sin distorsionar debajo. */
const FRAG_OUTPUT = `
precision highp float;
precision highp sampler2D;
varying vec2 vUv;
uniform float u_ratio;
uniform float u_img_ratio;
uniform float u_disturb_power;
uniform vec2 u_img_pos;
uniform sampler2D u_output_texture;
uniform sampler2D u_velocity_texture;
uniform sampler2D u_text_texture;

/* Encaje «cover» respetando object-position, no centrado a la fuerza.
   Con scale < 1 en un eje se recorta por ese eje, y el desplazamiento
   u_img_pos * (1 - scale) coloca la ventana visible donde la pone el CSS.
   Sin esto, la version distorsionada saltaba de encuadre respecto a la
   fotografia real en cuanto entraba el puntero.
   (Sin acentos ni comillas invertidas: esto vive dentro de una plantilla
   de JavaScript y una comilla invertida la cerraria a media cadena.) */
vec2 get_img_uv() {
  vec2 scale = vec2(1.0);
  if (u_ratio > u_img_ratio) scale.y = u_img_ratio / u_ratio;
  else scale.x = u_ratio / u_img_ratio;
  return vUv * scale + u_img_pos * (1.0 - scale);
}

void main () {
  float offset = texture2D(u_output_texture, vUv).r;
  vec2 velocity = texture2D(u_velocity_texture, vUv).xy;
  velocity += .001;

  vec2 img_uv = get_img_uv();
  img_uv -= 2.0 * u_disturb_power * normalize(velocity) * offset;

  vec3 img = texture2D(u_text_texture, clamp(img_uv, 0.0, 1.0)).rgb;
  gl_FragColor = vec4(img, 1.0);
}
`;

interface Props {
    /**
     * De dónde sale la textura. Si es un `<video>` se vuelve a subir el
     * fotograma en cada cuadro; si es una `<img>`, una sola vez al cargar.
     */
    fuenteRef: React.RefObject<HTMLVideoElement | HTMLImageElement | null>;
    /** Clases del lienzo. Debe quedar EXACTAMENTE sobre la fuente. */
    className?: string;
    /** `object-position` de la fuente, en 0–1. Por defecto, centrado. */
    posicion?: [number, number];
    /** Radio de influencia del cursor, en píxeles. */
    radio?: number;
    /** 0–100. Cuánto empuja el agua a los píxeles. */
    intensidad?: number;
}

const DistorsionLiquida: React.FC<Props> = ({
    fuenteRef,
    className = 'absolute inset-0 h-full w-full',
    posicion = [0.5, 0.5],
    radio = 95,
    intensidad = 32,
}) => {
    const lienzoRef = useRef<HTMLCanvasElement>(null);
    const [activo, setActivo] = useState(false);

    useEffect(() => {
        const lienzo = lienzoRef.current;
        const fuente = fuenteRef.current;
        /* ── Zona de escucha: la SECCIÓN entera, no el padre del lienzo ──
           El texto de la banda y su degradado son HERMANOS del contenedor de la
           imagen, no hijos. Escuchando en el padre del lienzo, todo puntero que
           cayera sobre las letras o sobre la parte baja oscurecida no llegaba
           nunca al efecto: una franja muerta justo donde más se mira.
           La sección sí es ancestro de todo, así que ahí llega todo. */
        const zona = lienzo?.closest('section') ?? lienzo?.parentElement ?? null;
        if (!lienzo || !zona || !fuente) return;

        // Sin ratón no hay puntero que seguir, y el coste no se justifica.
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

        const gl = lienzo.getContext('webgl', {
            alpha: false,
            antialias: false,
            premultipliedAlpha: false,
        }) as WebGLRenderingContext | null;
        if (!gl) return;
        // Los búferes de la simulación son de coma flotante. Sin la extensión
        // no hay simulación posible; se deja el vídeo tal cual.
        if (!gl.getExtension('OES_texture_float')) return;
        gl.getExtension('OES_texture_float_linear');

        const cp = intensidad / 100;
        const params = {
            radioCursor: radio,
            fuerzaCursor: 5 + ((cp - 0.1) * (50 - 5)) / (1 - 0.1),
            fuerzaDistorsion: intensidad / 100,
        };

        const puntero = { x: 0, y: 0, dx: 0, dy: 0, movido: false };
        const res = { w: 0, h: 0 };

        type FBO = { fbo: WebGLFramebuffer; width: number; height: number; attach(i: number): number };
        type DobleFBO = {
            texelSizeX: number; texelSizeY: number;
            read(): FBO; write(): FBO; swap(): void;
        };

        let color: DobleFBO;
        let velocidad: DobleFBO;
        let divergencia: FBO;
        let presion: DobleFBO;
        let texturaVideo: WebGLTexture | null = null;
        let raf: number | null = null;
        let dentro = false;
        let enPantalla = false;
        /* Si la textura no se puede leer (imagen sin CORS, contexto perdido…)
           el efecto se RETIRA solo y deja la foto intacta. Sin esto, la excepción
           mataba el bucle a media pasada y el lienzo se quedaba congelado y
           opaco encima de la fotografía — peor que no tener efecto. */
        let averiado = false;
        /* Todo lo que se reserva en la GPU se anota aquí. Liberarlo uno por uno
           es lo correcto; matar el contexto con `loseContext()` NO lo es —
           React monta el efecto dos veces en desarrollo (StrictMode) y la
           segunda pasada se encontraba el contexto ya muerto: `getExtension`
           devolvía null, el efecto salía antes de poner los oyentes, y el
           efecto no ocurría jamás. Costó encontrarlo porque no da ningún error. */
        const texturas: WebGLTexture[] = [];
        const marcos: WebGLFramebuffer[] = [];
        const programas: WebGLProgram[] = [];
        let restantes = 0;   // cuadros de cortesía para que el agua se asiente

        function compilar(fuente: string, tipo: number) {
            const sh = gl!.createShader(tipo)!;
            gl!.shaderSource(sh, fuente);
            gl!.compileShader(sh);
            if (!gl!.getShaderParameter(sh, gl!.COMPILE_STATUS)) {
                throw new Error(gl!.getShaderInfoLog(sh) || 'error de shader');
            }
            return sh;
        }

        function programa(vs: string, fs: string) {
            const prog = gl!.createProgram()!;
            gl!.attachShader(prog, compilar(vs, gl!.VERTEX_SHADER));
            gl!.attachShader(prog, compilar(fs, gl!.FRAGMENT_SHADER));
            gl!.bindAttribLocation(prog, 0, 'a_position');
            gl!.linkProgram(prog);
            if (!gl!.getProgramParameter(prog, gl!.LINK_STATUS)) {
                throw new Error(gl!.getProgramInfoLog(prog) || 'error de enlace');
            }
            programas.push(prog);
            const u: Record<string, WebGLUniformLocation | null> = {};
            const n = gl!.getProgramParameter(prog, gl!.ACTIVE_UNIFORMS) as number;
            for (let i = 0; i < n; i++) {
                const a = gl!.getActiveUniform(prog, i);
                if (a) u[a.name] = gl!.getUniformLocation(prog, a.name);
            }
            return { program: prog, u };
        }

        /* El original creaba dos búferes NUEVOS en cada `blit`, o sea decenas
           por fotograma, y nunca los liberaba. Aquí se crean una vez. */
        const vbo = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]), gl.STATIC_DRAW);
        const ebo = gl.createBuffer();
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ebo);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 0, 2, 3]), gl.STATIC_DRAW);
        gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
        gl.enableVertexAttribArray(0);

        function blit(destino: FBO | null) {
            if (!destino) {
                gl!.viewport(0, 0, gl!.drawingBufferWidth, gl!.drawingBufferHeight);
                gl!.bindFramebuffer(gl!.FRAMEBUFFER, null);
            } else {
                gl!.viewport(0, 0, destino.width, destino.height);
                gl!.bindFramebuffer(gl!.FRAMEBUFFER, destino.fbo);
            }
            gl!.drawElements(gl!.TRIANGLES, 6, gl!.UNSIGNED_SHORT, 0);
        }

        function crearFBO(w: number, h: number): FBO {
            gl!.activeTexture(gl!.TEXTURE0);
            const tex = gl!.createTexture()!;
            gl!.bindTexture(gl!.TEXTURE_2D, tex);
            gl!.texParameteri(gl!.TEXTURE_2D, gl!.TEXTURE_MIN_FILTER, gl!.LINEAR);
            gl!.texParameteri(gl!.TEXTURE_2D, gl!.TEXTURE_MAG_FILTER, gl!.LINEAR);
            gl!.texParameteri(gl!.TEXTURE_2D, gl!.TEXTURE_WRAP_S, gl!.CLAMP_TO_EDGE);
            gl!.texParameteri(gl!.TEXTURE_2D, gl!.TEXTURE_WRAP_T, gl!.CLAMP_TO_EDGE);
            gl!.texImage2D(gl!.TEXTURE_2D, 0, gl!.RGB, w, h, 0, gl!.RGB, gl!.FLOAT, null);
            texturas.push(tex);
            const fbo = gl!.createFramebuffer()!;
            marcos.push(fbo);
            gl!.bindFramebuffer(gl!.FRAMEBUFFER, fbo);
            gl!.framebufferTexture2D(gl!.FRAMEBUFFER, gl!.COLOR_ATTACHMENT0, gl!.TEXTURE_2D, tex, 0);
            gl!.viewport(0, 0, w, h);
            gl!.clear(gl!.COLOR_BUFFER_BIT);
            return {
                fbo, width: w, height: h,
                attach(i: number) {
                    gl!.activeTexture(gl!.TEXTURE0 + i);
                    gl!.bindTexture(gl!.TEXTURE_2D, tex);
                    return i;
                },
            };
        }

        function crearDoble(w: number, h: number): DobleFBO {
            let a = crearFBO(w, h);
            let b = crearFBO(w, h);
            return {
                texelSizeX: 1 / w, texelSizeY: 1 / h,
                read: () => a, write: () => b,
                swap() { const t = a; a = b; b = t; },
            };
        }

        const pSplat = programa(VERT, FRAG_POINT);
        const pDiv = programa(VERT, FRAG_DIVERGENCE);
        const pPres = programa(VERT, FRAG_PRESSURE);
        const pGrad = programa(VERT, FRAG_GRAD_SUB);
        const pAdv = programa(VERT, FRAG_ADVECT);
        const pOut = programa(VERT, FRAG_OUTPUT);

        function medir() {
            const w = lienzo!.clientWidth || zona!.clientWidth;
            const h = lienzo!.clientHeight || zona!.clientHeight;
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            lienzo!.width = Math.max(2, Math.round(w * dpr));
            lienzo!.height = Math.max(2, Math.round(h * dpr));
            /* La rejilla de la simulación va a 256 de alto, no a 512. El agua no
               gana nada visible al doble de resolución y cuesta cuatro veces
               más: son cuatro pasadas completas más 16 de presión por cuadro. */
            const base = 256;
            res.w = Math.max(2, Math.round(base * (w / Math.max(1, h))));
            res.h = base;
            color = crearDoble(res.w, res.h);
            velocidad = crearDoble(res.w, res.h);
            divergencia = crearFBO(res.w, res.h);
            presion = crearDoble(res.w, res.h);
        }

        medir();
        const observadorTam = new ResizeObserver(medir);
        observadorTam.observe(lienzo);

        texturaVideo = gl.createTexture();
        if (texturaVideo) texturas.push(texturaVideo);
        gl.bindTexture(gl.TEXTURE_2D, texturaVideo);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        let imagenSubida = false;

        function esVideo(el: Element | null): el is HTMLVideoElement {
            return !!el && el.tagName === 'VIDEO';
        }

        function medidaFuente() {
            const f = fuenteRef.current;
            if (esVideo(f)) return { w: f.videoWidth, h: f.videoHeight };
            const i = f as HTMLImageElement | null;
            return { w: i?.naturalWidth || 0, h: i?.naturalHeight || 0 };
        }

        function subirFotograma() {
            const f = fuenteRef.current;
            if (!f) return false;
            if (esVideo(f)) {
                if (f.readyState < 2 || !f.videoWidth) return false;
            } else {
                // Una imagen no cambia: subirla en cada cuadro sería tirar
                // ancho de banda de GPU para copiar lo mismo sesenta veces.
                if (imagenSubida) return true;
                const i = f as HTMLImageElement;
                if (!i.complete || !i.naturalWidth) return false;
                imagenSubida = true;
            }
            gl!.activeTexture(gl!.TEXTURE0);
            gl!.bindTexture(gl!.TEXTURE_2D, texturaVideo);
            // `UNPACK_FLIP_Y` evita invertir la V en el shader, que es donde el
            // original acababa con un `1.0 - uv.y` disperso por todas partes.
            gl!.pixelStorei(gl!.UNPACK_FLIP_Y_WEBGL, 1);
            try {
                gl!.texImage2D(gl!.TEXTURE_2D, 0, gl!.RGBA, gl!.RGBA, gl!.UNSIGNED_BYTE, f);
            } catch {
                averiado = true;
                return false;
            } finally {
                gl!.pixelStorei(gl!.UNPACK_FLIP_Y_WEBGL, 0);
            }
            return true;
        }

        function dibujar() {
            const dt = 1 / 60;
            const m = medidaFuente();
            const razonLienzo = lienzo!.clientWidth / Math.max(1, lienzo!.clientHeight);
            const razonFuente = m.w && m.h ? m.w / m.h : 16 / 9;

            if (puntero.movido) {
                puntero.movido = false;
                gl!.useProgram(pSplat.program);
                gl!.uniform1i(pSplat.u.u_input_texture!, velocidad.read().attach(1));
                gl!.uniform1f(pSplat.u.u_ratio!, razonLienzo);
                /* El puntero se mapea contra el LIENZO, no contra la sección:
                   el lienzo sobresale un 7 % por arriba y abajo para el
                   paralaje, así que sus coordenadas no son las de la sección. */
                const u = puntero.x / Math.max(1, lienzo!.clientWidth);
                const w = 1 - puntero.y / Math.max(1, lienzo!.clientHeight);
                gl!.uniform2f(pSplat.u.u_point!, u, w);
                gl!.uniform3f(pSplat.u.u_point_value!, puntero.dx, -puntero.dy, 0);
                const rr = params.radioCursor / Math.max(1, lienzo!.clientHeight);
                gl!.uniform1f(pSplat.u.u_point_size!, rr * rr);
                blit(velocidad.write()); velocidad.swap();

                gl!.uniform1i(pSplat.u.u_input_texture!, color.read().attach(1));
                gl!.uniform3f(pSplat.u.u_point_value!, params.fuerzaCursor * 0.001, 0, 0);
                blit(color.write()); color.swap();
            }

            gl!.useProgram(pDiv.program);
            gl!.uniform2f(pDiv.u.u_texel!, velocidad.texelSizeX, velocidad.texelSizeY);
            gl!.uniform1i(pDiv.u.u_velocity_texture!, velocidad.read().attach(1));
            blit(divergencia);

            gl!.useProgram(pPres.program);
            gl!.uniform2f(pPres.u.u_texel!, velocidad.texelSizeX, velocidad.texelSizeY);
            gl!.uniform1i(pPres.u.u_divergence_texture!, divergencia.attach(1));
            for (let i = 0; i < 16; i++) {
                gl!.uniform1i(pPres.u.u_pressure_texture!, presion.read().attach(2));
                blit(presion.write()); presion.swap();
            }

            gl!.useProgram(pGrad.program);
            gl!.uniform2f(pGrad.u.u_texel!, velocidad.texelSizeX, velocidad.texelSizeY);
            gl!.uniform1i(pGrad.u.u_pressure_texture!, presion.read().attach(1));
            gl!.uniform1i(pGrad.u.u_velocity_texture!, velocidad.read().attach(2));
            blit(velocidad.write()); velocidad.swap();

            gl!.useProgram(pAdv.program);
            gl!.uniform2f(pAdv.u.u_texel!, velocidad.texelSizeX, velocidad.texelSizeY);
            gl!.uniform2f(pAdv.u.u_output_textel!, velocidad.texelSizeX, velocidad.texelSizeY);
            gl!.uniform1i(pAdv.u.u_velocity_texture!, velocidad.read().attach(1));
            gl!.uniform1i(pAdv.u.u_input_texture!, velocidad.read().attach(1));
            gl!.uniform1f(pAdv.u.u_dt!, dt);
            gl!.uniform1f(pAdv.u.u_dissipation!, 0.97);
            blit(velocidad.write()); velocidad.swap();

            gl!.uniform2f(pAdv.u.u_output_textel!, color.texelSizeX, color.texelSizeY);
            gl!.uniform1i(pAdv.u.u_input_texture!, color.read().attach(2));
            gl!.uniform1f(pAdv.u.u_dt!, 8 * dt);
            gl!.uniform1f(pAdv.u.u_dissipation!, 0.98);
            blit(color.write()); color.swap();

            subirFotograma();

            gl!.useProgram(pOut.program);
            gl!.uniform1i(pOut.u.u_velocity_texture!, velocidad.read().attach(2));
            gl!.uniform1i(pOut.u.u_output_texture!, color.read().attach(1));
            gl!.uniform1f(pOut.u.u_ratio!, razonLienzo);
            gl!.uniform1f(pOut.u.u_img_ratio!, razonFuente);
            gl!.uniform1f(pOut.u.u_disturb_power!, params.fuerzaDistorsion);
            /* La V está volteada al subir la textura, así que el eje vertical
               del `object-position` de CSS (que mide desde arriba) hay que
               invertirlo aquí. */
            gl!.uniform2f(pOut.u.u_img_pos!, posicion[0], 1 - posicion[1]);
            gl!.activeTexture(gl!.TEXTURE0);
            gl!.bindTexture(gl!.TEXTURE_2D, texturaVideo);
            gl!.uniform1i(pOut.u.u_text_texture!, 0);
            blit(null);
        }

        function bucle() {
            dibujar();
            if (averiado) {
                raf = null;
                setActivo(false);
                return;
            }
            if (!dentro && restantes > 0) restantes--;
            if (enPantalla && (dentro || restantes > 0)) {
                raf = requestAnimationFrame(bucle);
            } else {
                raf = null;
                setActivo(false);
            }
        }

        function arrancar() {
            if (raf != null || averiado) return;
            setActivo(true);
            raf = requestAnimationFrame(bucle);
        }

        const alEntrar = () => {
            dentro = true;
            restantes = 90;
            if (enPantalla) arrancar();
        };
        const alSalir = () => { dentro = false; };
        const alMover = (e: PointerEvent) => {
            const r = lienzo!.getBoundingClientRect();
            const x = e.clientX - r.left;
            const y = e.clientY - r.top;
            puntero.dx = 6 * (x - puntero.x);
            puntero.dy = 6 * (y - puntero.y);
            puntero.x = x;
            puntero.y = y;
            puntero.movido = true;
            dentro = true;
            restantes = 90;
            if (enPantalla) arrancar();
        };

        zona.addEventListener('pointerenter', alEntrar);
        zona.addEventListener('pointerleave', alSalir);
        zona.addEventListener('pointermove', alMover, { passive: true });

        const vigia = new IntersectionObserver(([e]) => {
            enPantalla = e.isIntersecting;
            if (!enPantalla) { dentro = false; restantes = 0; }
        });
        vigia.observe(zona);

        return () => {
            if (raf != null) cancelAnimationFrame(raf);
            zona.removeEventListener('pointerenter', alEntrar);
            zona.removeEventListener('pointerleave', alSalir);
            zona.removeEventListener('pointermove', alMover);
            vigia.disconnect();
            observadorTam.disconnect();
            // Se libera lo reservado, pero el CONTEXTO se queda vivo.
            texturas.forEach((t) => gl.deleteTexture(t));
            marcos.forEach((f) => gl.deleteFramebuffer(f));
            programas.forEach((pr) => gl.deleteProgram(pr));
            gl.deleteBuffer(vbo);
            gl.deleteBuffer(ebo);
        };
    }, [fuenteRef, radio, intensidad, posicion]);

    /* Invisible en reposo: el vídeo real se ve directamente y la GPU no hace
       nada. Al entrar el puntero, el lienzo aparece pintando ese MISMO
       fotograma, así que el relevo no se nota. */
    return (
        <canvas
            ref={lienzoRef}
            aria-hidden="true"
            className={`pointer-events-none transition-opacity duration-300 ${className} ${
                activo ? 'opacity-100' : 'opacity-0'
            }`}
        />
    );
};

export default DistorsionLiquida;
